import { Table as MuiTable, TableHead, TableRow, TableCell, TableBody, Skeleton, Checkbox, Stack } from '@mui/material';

import { getFragmentData } from '@api/gql';
import { NoResults, TableWrapper } from '@components';
import { StandingAccessReportEntitlementLinkFragmentFragment } from '@api/gql/graphql';
import { RiskScoreItem } from '@common/components/RiskScore';
import PrimarySecondary from '@common/ui/PrimarySecondary';

import {
  standingAccessReportEntitlementLinkedElementFragmentDocument,
  standingAccessReportPermissionFragmentDocument,
  standingAccessReportResourceFragmentDocument,
} from '@StandingAccess/services/gql';
import AssessmentReportLastUsed from './AssessmentReportLastUsed';
import AssessmentReportConnection from './AssessmentReportConnection';
import { AssessmentTierWidgetLabel } from './AssessmentTierWidgetLabel';
import { ReportTierOptions } from '@StandingAccess/types';

interface AssessmentReportAccessRevokeTableProps {
  isLoading?: boolean;
  isFetching?: boolean;
  items: StandingAccessReportEntitlementLinkFragmentFragment[];
  selected: string[];
  realSelectedAll: boolean;
  onSelect: (itemId: string) => void;
  onSelectAll: () => void;
}

function useAssessmentReportAccessRevokeTableColumns() {
  return [
    { key: 'permission_set', label: 'Permission Set' },
    { key: 'riskScore', label: 'Risk Score', width: 150 },
    { key: 'account', label: 'Account' },
    { key: 'identity', label: 'Connected Identity' },
    { key: 'tier', label: 'Tier', width: 150 },
    { key: 'lastUsed', label: 'Last Used', width: 150 },
  ];
}

export default function AssessmentReportAccessRevokeTable({
  isLoading,
  isFetching,
  items,
  selected,
  realSelectedAll,
  onSelect,
  onSelectAll,
}: AssessmentReportAccessRevokeTableProps) {
  const columns = useAssessmentReportAccessRevokeTableColumns();

  if (items.length === 0) return <NoResults>No entitlements found</NoResults>;

  const isSelectedAllOnPage =
    selected.filter((id) => items.map((item) => item.id).includes(id)).length === items.length || realSelectedAll;

  return (
    <TableWrapper loading={isLoading} noBackground>
      <MuiTable>
        <TableHead>
          <TableRow>
            <TableCell component="th" size="medium" width={50}>
              <Checkbox size="small" onChange={onSelectAll} checked={isSelectedAllOnPage} disabled={realSelectedAll} />
            </TableCell>
            {columns.map((column) => (
              <TableCell component="th" size="medium" key={column.key} style={{ width: column.width }}>
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isFetching ? (
            <>
              <AssessmentReportTableItemLoadingRow />
              <AssessmentReportTableItemLoadingRow />
              <AssessmentReportTableItemLoadingRow />
            </>
          ) : (
            items.map((item) => (
              <AssessmentReportTableItem
                key={item.id}
                item={item}
                onSelect={() => onSelect(item.id)}
                selected={selected.includes(item.id) || realSelectedAll}
                disableSelect={realSelectedAll}
              />
            ))
          )}
        </TableBody>
      </MuiTable>
    </TableWrapper>
  );
}

interface AssessmentReportTableItemProps {
  item: StandingAccessReportEntitlementLinkFragmentFragment;
  selected?: boolean;
  onSelect: () => void;
  disableSelect?: boolean;
}

export function AssessmentReportTableItem({ item, onSelect, selected, disableSelect }: AssessmentReportTableItemProps) {
  const permission = getFragmentData(standingAccessReportPermissionFragmentDocument, item.permission);
  const resource = getFragmentData(standingAccessReportResourceFragmentDocument, item.resource);
  const connection = getFragmentData(standingAccessReportEntitlementLinkedElementFragmentDocument, item.linkedElement);

  return (
    <TableRow
      hover
      className="MuiTableRow-clickable"
      onClick={disableSelect ? undefined : onSelect}
      selected={selected}
    >
      <TableCell component="th" size="medium">
        <Checkbox size="small" checked={selected} disabled={disableSelect} />
      </TableCell>
      <TableCell component="th" size="medium">
        {permission?.name}
      </TableCell>
      <TableCell component="th" size="medium">
        <RiskScoreItem riskScore={item.riskScore} maxRiskScore={9} withRiskScore />
      </TableCell>
      <TableCell component="th" size="medium">
        <PrimarySecondary icon={resource?.type.icons.svg} primary={resource?.name} />
      </TableCell>
      <TableCell component="th" size="medium">
        <AssessmentReportConnection connection={connection} />
      </TableCell>
      <TableCell component="th" size="medium">
        <Stack direction="column" spacing={0.5}>
          {item.tiers.map((tier) => (
            <AssessmentTierWidgetLabel key={tier} type={tier as ReportTierOptions} />
          ))}
        </Stack>
      </TableCell>
      <TableCell component="th" size="medium">
        <AssessmentReportLastUsed lastUsedAt={item.lastUsedAt} />
      </TableCell>
    </TableRow>
  );
}

export function AssessmentReportTableItemLoadingRow() {
  const columns = useAssessmentReportAccessRevokeTableColumns();
  return (
    <TableRow>
      {Array.from({ length: columns.length + 1 }).map((_, index) => (
        <TableCell key={index}>
          <Skeleton variant="text" width="100%" />
        </TableCell>
      ))}
    </TableRow>
  );
}
